import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import IDaily_Route from "../../../models/IDaily_Route";
import IDaily_Route_Order_Map from "../../../models/IDaily_Route_Order_Map";
import IOrder from "../../../models/IOrder";
import IVendorModel from "../../../models/IVendorModel";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import INewDaily_Route from "../../../models/INewDaily_Route";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import { useFormik } from "formik";
import OwDateInput from "../../shared/OwDateInput/OwDateInput";
import { Moment } from "moment-timezone";
import IProcessDailyRouteDTO, { IStopSequenceRef, IStopServiceTimeEntry } from "../../../models/DTO/IProcessDailyRouteDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnProcessDailyRouteDTO from "../../../models/DTO/IOnProcessDailyRouteDTO";
import IGetVendorsDTO from "../../../models/DTO/IGetVendorsDTO";
import IOnGetVendorsDTO from "../../../models/DTO/IOnGetVendorsDTO";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IVehicle from "../../../models/IVehicle";
import IOnGetVehiclesDTO from "../../../models/DTO/IOnGetVehiclesDTO";
import IOnGetRouteTemplatesDTO, { IRouteTemplateResponse } from "../../../models/DTO/IOnGetRouteTemplatesDTO";
import IConnection_Token from "../../../models/IConnection_Token";
import IOnTypeaheadLookupByTokenTypeDTO from "../../../models/DTO/IOnTypeaheadLookupByTokenTypeDTO";
import TokenTypesEnum from "../../../models/TokenTypesEnum";
import IUser from "../../../models/IUser";
import { TypeaheadFn } from "../../shared/TypeaheadFn/TypeaheadFn";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import "./DailyRouteSidebar.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwPill from "@components/shared/OwPill/OwPill";
import DailyRouteService from "../../../services/DailyRouteService";
import { DailyRouteStatusWarningModal } from "../DailyRouteStatusWarningModal/DailyRouteStatusWarningModal";
import { DailyRouteDoNotShipWarningModal } from "../DailyRouteDoNotShipWarningModal/DailyRouteDoNotShipWarningModal";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import { useIntl } from "react-intl";

type Props = {
    dailyRoute?: IDaily_Route;
    cancelFn: (refresh?: boolean) => void;
};

// One row per stop: an order stop (order set) or an order-less customer stop
// (vendor set). Keys are namespaced because a customer can have both an order
// stop and a customer stop on the same route.
interface IStopRowState {
    key: string; // "order-<guid>" | "vendor-<guid>"
    mapRowId?: number; // Daily_Route_Order_Maps[].id for existing rows
    order?: IOrder;
    vendor?: IVendorModel;
    status: "existing" | "added" | "removed";
    // Persisted Daily_Route_Order_Maps.service_minutes (null = inherit). Pending
    // edits live in serviceTimeOverrides, keyed by `key`, until saved.
    serviceMinutes?: number | null;
}

// Draggable/droppable stop row. Dropping ON a row inserts the dragged row before it.
const DraggableStopRow = ({
    stop,
    index,
    canDrag,
    onDropOn,
    children,
}: {
    stop: IStopRowState;
    index: number;
    canDrag: boolean;
    onDropOn: (sourceKey: string, targetKey: string) => void;
    children: React.ReactNode;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    // Drags start only from the grip so mouse-down/typing in the per-stop
    // service-minutes input never begins a drag.
    const handleRef = useRef<HTMLElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isDraggedOver, setIsDraggedOver] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || !canDrag) return;

        const cleanupDraggable = draggable({
            element: el,
            dragHandle: handleRef.current ?? undefined,
            getInitialData: () => ({ stopKey: stop.key }),
            onDragStart: () => setIsDragging(true),
            onDrop: () => setIsDragging(false),
        });

        const cleanupDropTarget = dropTargetForElements({
            element: el,
            getData: () => ({ stopKey: stop.key }),
            onDragEnter: () => setIsDraggedOver(true),
            onDragLeave: () => setIsDraggedOver(false),
            onDrop: ({ source }) => {
                setIsDraggedOver(false);
                const { stopKey } = source.data as { stopKey: string };
                if (stopKey !== stop.key) {
                    onDropOn(stopKey, stop.key);
                }
            },
        });

        return () => {
            cleanupDraggable();
            cleanupDropTarget();
        };
    }, [stop.key, canDrag, onDropOn]);

    return (
        <div
            ref={ref}
            className={`order-item d-flex align-items-center ${stop.status} ${isDragging ? "dragging" : ""} ${
                isDraggedOver ? "drag-over" : ""
            }`}>
            {canDrag && <i ref={handleRef} className="fas fa-grip-vertical drag-grip me-2 text-muted" />}
            <span className="stop-seq me-2">{index + 1}</span>
            {children}
        </div>
    );
};

export const DailyRouteSidebar = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [dailyRoute] = useState<IDaily_Route | INewDaily_Route | undefined>(props.dailyRoute);
    const [isDisabled, setIsDisabled] = useState(true);
    const [vehicles, setVehicles] = useState<IVehicle[]>([]);
    const [availableUsers, setAvailableUsers] = useState<IConnection_Token[]>([]);
    const [selectedDriver, setSelectedDriver] = useState<IUser | undefined>(props.dailyRoute?.Driver ? props.dailyRoute.Driver : undefined);

    // Route selection state
    const [availableRoutes, setAvailableRoutes] = useState<IRouteTemplateResponse[]>([]);
    const [isLoadingRoutes, setIsLoadingRoutes] = useState<boolean>(true);
    const [selectedRouteId, setSelectedRouteId] = useState<number | undefined>(props.dailyRoute?.route_id);

    // Stop management state (order stops + order-less customer stops in one ordered list)
    const [stopStates, setStopStates] = useState<IStopRowState[]>(() => {
        if (!props.dailyRoute?.Daily_Route_Order_Maps) {
            return [];
        }

        // Initialize in sequence order, not map order: null/-1 seq last, name tiebreak.
        const seqOf = (map: IDaily_Route_Order_Map) =>
            map.optimized_order_seq === null || map.optimized_order_seq === undefined || map.optimized_order_seq === -1
                ? Number.MAX_SAFE_INTEGER
                : map.optimized_order_seq;
        const nameOf = (map: IDaily_Route_Order_Map) => map.Order?.name || map.Vendor?.name || "";

        return [...props.dailyRoute.Daily_Route_Order_Maps]
            .sort((a, b) => seqOf(a) - seqOf(b) || nameOf(a).localeCompare(nameOf(b)))
            .map((map) => {
                const serviceMinutes = map.service_minutes ?? null;
                if (map.Order) {
                    return {
                        key: `order-${map.Order.guid}`,
                        mapRowId: map.id,
                        order: map.Order,
                        status: "existing" as const,
                        serviceMinutes,
                    };
                }
                if (map.Vendor) {
                    return {
                        key: `vendor-${map.Vendor.guid}`,
                        mapRowId: map.id,
                        vendor: map.Vendor,
                        status: "existing" as const,
                        serviceMinutes,
                    };
                }
                return null;
            })
            .filter(Boolean) as IStopRowState[];
    });
    const [hasReordered, setHasReordered] = useState(false);
    // Pending per-stop service-minute edits keyed by IStopRowState.key. A key is
    // present only while the field differs from the persisted value (null =
    // inherit), so an empty map means there is nothing to send.
    const [serviceTimeOverrides, setServiceTimeOverrides] = useState<Record<string, number | null>>({});
    const hasServiceTimeChanges = Object.keys(serviceTimeOverrides).length > 0;
    const [seedRouteCustomers, setSeedRouteCustomers] = useState(false);
    const [availableCustomers, setAvailableCustomers] = useState<IVendorModel[]>([]);
    const [resetCustomerTypeahead, setResetCustomerTypeahead] = useState(false);
    const [searchResults, setSearchResults] = useState<IOrder[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [showStatusWarningModal, setShowStatusWarningModal] = useState(false);
    const [ordersNeedingStatusChange, setOrdersNeedingStatusChange] = useState<IOrder[]>([]);
    const [pendingFormValues, setPendingFormValues] = useState<{
        route_date: string;
        route_id: number | undefined;
        vehicle_id: number | undefined;
    } | null>(null);
    const [showDoNotShipWarningModal, setShowDoNotShipWarningModal] = useState(false);
    const [doNotShipExcludedOrders, setDoNotShipExcludedOrders] = useState<IOrder[]>([]);
    const [pendingExcludedIds, setPendingExcludedIds] = useState<Set<number>>(new Set());

    const getVehicles = async () => {
        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.GetVehicles,
            query: { limit: 100, offset: 0 },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });
            const data: IOnGetVehiclesDTO = await response.json();
            setVehicles(data.vehicles);
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        }
    };

    const getRoutes = async () => {
        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.GetRouteTemplates,
                query: {},
            });
            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "GET", headers });
            const data: IOnGetRouteTemplatesDTO = await response.json();
            setAvailableRoutes(data.routes);
        } catch (error) {
            console.error("Error fetching routes:", error);
        } finally {
            setIsLoadingRoutes(false);
        }
    };

    const searchUsers = async (name: string) => {
        if (!name.trim()) {
            setAvailableUsers([]);
            return;
        }

        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.TokensSearch,
            query: {
                tokenType: TokenTypesEnum.SUPPLIER_USER,
                name,
                limit: 10,
            },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });
            if (response.ok) {
                const data = (await response.json()) as IOnTypeaheadLookupByTokenTypeDTO;
                setAvailableUsers(data.results || []);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setAvailableUsers([]);
        }
    };

    const handleDriverSelected = (user: IUser) => {
        setSelectedDriver(user);
        setAvailableUsers([]);
    };

    const clearDriver = () => {
        setSelectedDriver(undefined);
    };

    const renderAvailableUser = (user: IUser) => {
        return (
            <div>
                <div>{user?.name}</div>
                {user?.email && <small className="text-muted">{user.email}</small>}
            </div>
        );
    };

    // Customer (order-less stop) search via the canonical socket vendor lookup.
    // nameGuidOnly is intentionally NOT set: it strips ship_to_id, which we need
    // to warn on customers without an address.
    const searchCustomers = (name: string) => {
        if (!name.trim()) {
            setAvailableCustomers([]);
            return;
        }

        const payload: IGetVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // The server splits queryString on & and = before decoding each value, so encode the
            // name (same as the other VendorsGetVendors typeaheads) to keep it one parameter.
            queryString: `?name=${encodeURIComponent(name)}`,
            limit: 10,
        };

        socket.emit(EndpointsEnum.VendorsGetVendors, payload);
    };

    const onGetVendors = (data: IOnGetVendorsDTO) => {
        const vendors = (data.supplierVendorTokens || []).map((token) => token.Vendor).filter(Boolean) as IVendorModel[];
        setAvailableCustomers(vendors);
    };

    const handleCustomerSelected = (vendor: IVendorModel) => {
        setStopStates((prev) => {
            const existingRow = prev.find((stop) => stop.vendor?.id === vendor.id);

            if (existingRow) {
                // Restore instead of duplicating (also dedupes an already-active row)
                if (existingRow.status === "removed") {
                    return prev.map((stop) => (stop.key === existingRow.key ? { ...stop, status: "existing" as const } : stop));
                }
                return prev;
            }

            return [...prev, { key: `vendor-${vendor.guid}`, vendor, status: "added" as const }];
        });
        setAvailableCustomers([]);
        setResetCustomerTypeahead(true);
    };

    const renderAvailableCustomer = (vendor: IVendorModel) => {
        return (
            <div>
                <div>{vendor?.name}</div>
                {vendor?.Ship_To && (
                    <small className="text-muted">
                        {vendor.Ship_To.city}, {vendor.Ship_To.state}
                    </small>
                )}
            </div>
        );
    };

    // Search orders via REST API with debounce
    const searchOrders = useCallback(
        async (query: string) => {
            if (!query.trim()) {
                setSearchResults([]);
                setIsSearching(false);
                return;
            }

            setIsSearching(true);

            try {
                const url = buildRestUrlWithQuery({
                    endpoint: RestEndpointsEnum.GetOrdersWithAuth,
                    query: {
                        queryString: `order=${query}`,
                        limit: 10,
                    },
                });

                const headers = buildAuthHeadersForFetch({
                    userModel,
                    supplierModel,
                });

                const response = await fetch(url, { method: "GET", headers });

                if (response.ok) {
                    const data = await response.json();

                    // Filter out orders already in the list (not removed)
                    const existingOrderIds = stopStates
                        .filter((stop) => stop.order && stop.status !== "removed")
                        .map((stop) => stop.order!.id);

                    setSearchResults(data.orders.filter((o: IOrder) => !existingOrderIds.includes(o.id)));
                }
            } catch (error) {
                console.error("Error searching orders:", error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        },
        [userModel, supplierModel, stopStates]
    );

    // Debounced search handler
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debounce
        searchTimeoutRef.current = setTimeout(() => {
            searchOrders(query);
        }, 300);
    };

    // Add order to the list
    const addOrder = (order: IOrder) => {
        // Check "Do Not Ship Before" date against route date
        const routeDate = formik.values.route_date;
        if (routeDate && DailyRouteService.isBeforeDoNotShipDate(order, routeDate)) {
            const notification: INotification = {
                message: `Cannot add "${order.name}". "Do Not Ship Before" date is after the route date.`,
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
                skipMessageTranslate: true,
            };
            uiContext.notifyFn(null, notification);
            return;
        }
        const key = `order-${order.guid}`;
        setStopStates((prev) => {
            const existingRow = prev.find((stop) => stop.key === key);

            if (existingRow) {
                // Restore instead of duplicating (also dedupes an already-active row)
                if (existingRow.status === "removed") {
                    return prev.map((stop) => (stop.key === key ? { ...stop, status: "existing" as const } : stop));
                }
                return prev;
            }

            return [...prev, { key, order, status: "added" as const }];
        });
        setSearchResults([]);
        setSearchQuery("");
    };

    // Forget pending service-minute edits for rows leaving the active list.
    const dropServiceOverrides = (keys: string[]) => {
        setServiceTimeOverrides((prev) => {
            if (!keys.some((key) => key in prev)) return prev;
            const next = { ...prev };
            for (const key of keys) {
                delete next[key];
            }
            return next;
        });
    };

    // Per-stop service minutes edit. "" clears the override (inherit); anything
    // else is clamped to a non-negative integer (0 is a valid "drop and go").
    // Typing the persisted value back drops the pending edit so nothing is sent.
    const setServiceOverride = (stop: IStopRowState, raw: string) => {
        let next: number | null = null;
        if (raw !== "") {
            const parsed = Number(raw);
            if (Number.isNaN(parsed)) return;
            next = Math.max(0, Math.round(parsed));
        }

        const persisted = stop.serviceMinutes ?? null;
        if (next === persisted) {
            dropServiceOverrides([stop.key]);
            return;
        }

        setServiceTimeOverrides((prev) => ({ ...prev, [stop.key]: next }));
    };

    // Remove a stop (order or customer) from the list
    const removeStop = (key: string) => {
        dropServiceOverrides([key]);
        setStopStates(
            (prev) =>
                prev
                    .map((stop) => {
                        if (stop.key !== key) return stop;

                        // If it was just added, remove it entirely
                        if (stop.status === "added") {
                            return null;
                        }

                        // If existing, mark as removed (strikethrough)
                        return { ...stop, status: "removed" as const };
                    })
                    .filter(Boolean) as IStopRowState[]
        );
    };

    // Restore a removed stop
    const restoreStop = (key: string) => {
        setStopStates((prev) =>
            prev.map((stop) => {
                if (stop.key !== key) return stop;
                return { ...stop, status: "existing" as const };
            })
        );
    };

    // Reorder: dropping a row ON another row inserts it before that row.
    const moveStop = useCallback((sourceKey: string, targetKey: string) => {
        setStopStates((prev) => {
            const active = prev.filter((stop) => stop.status !== "removed");
            const removed = prev.filter((stop) => stop.status === "removed");

            const fromIndex = active.findIndex((stop) => stop.key === sourceKey);
            const toIndex = active.findIndex((stop) => stop.key === targetKey);
            if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return prev;

            const next = [...active];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(
                next.findIndex((stop) => stop.key === targetKey),
                0,
                moved
            );

            return [...next, ...removed];
        });
        setHasReordered(true);
    }, []);

    const cancel = () => {
        props.cancelFn();
    };

    // Route selection helpers
    const canChangeRoute = (): boolean => {
        if (!props.dailyRoute) return true;
        const status = props.dailyRoute.status;
        return !status || status === "not_started";
    };

    // Manual stop reordering is also allowed mid-route.
    const canReorder = (): boolean => {
        if (!props.dailyRoute) return true;
        const status = props.dailyRoute.status;
        return !status || status === "not_started" || status === "in_progress";
    };

    const getCurrentRouteName = (): string => {
        if (props.dailyRoute?.Route?.name) return props.dailyRoute.Route.name;
        const route = availableRoutes.find((r) => r.id === selectedRouteId);
        return route?.name || "";
    };

    const handleRouteChange = (routeId: number) => {
        const selectedRoute = availableRoutes.find((r) => r.id === routeId);
        if (!selectedRoute) return;

        setSelectedRouteId(routeId);

        // Auto-populate driver if route has default
        if (selectedRoute.default_driver) {
            setSelectedDriver({
                id: selectedRoute.default_driver.id,
                guid: selectedRoute.default_driver.guid,
                name: selectedRoute.default_driver.name,
            } as IUser);
        }

        // Auto-populate vehicle if route has default
        if (selectedRoute.default_vehicle) {
            formik.setFieldValue("vehicle_id", selectedRoute.default_vehicle.id);
        }
    };

    // Ref for the stopSequence / stopServiceTimes channels: existing rows by map
    // row id, same-submit orders by order id, same-submit customers by vendor id.
    const toStopRef = (stop: IStopRowState): IStopSequenceRef => {
        if (stop.mapRowId) return { stop_id: stop.mapRowId };
        if (stop.order) return { order_id: stop.order.id };
        return { vendor_id: stop.vendor!.id };
    };

    const processDailyRoute = (values: { route_date: string; route_id: number | undefined; vehicle_id: number | undefined }) => {
        // Order channel (unchanged): add/remove by order id
        const addedOrderIds = stopStates.filter((stop) => stop.status === "added" && stop.order).map((stop) => stop.order!.id);
        const removedOrderIds = stopStates.filter((stop) => stop.status === "removed" && stop.order).map((stop) => stop.order!.id);

        // Customer channel: add by vendor id, remove by map row id (order-less rows only)
        const addedCustomerIds = stopStates.filter((stop) => stop.status === "added" && stop.vendor).map((stop) => stop.vendor!.id);
        const removedStopIds = stopStates
            .filter((stop) => stop.status === "removed" && stop.vendor && stop.mapRowId)
            .map((stop) => stop.mapRowId!);

        // stopSequence is a FULL re-statement of the route's stop order (seq = array
        // index) — only send it when the dispatcher actually reordered, otherwise it
        // would needlessly re-write the optimizer's sequence on every save. Also omit
        // it when seeding route customers: the server adds stops the client can't
        // list, so any sequence sent alongside would be a partial ordering.
        const stopSequence: IStopSequenceRef[] | undefined =
            hasReordered && !seedRouteCustomers ? stopStates.filter((stop) => stop.status !== "removed").map(toStopRef) : undefined;

        // stopServiceTimes is a SPARSE patch: only stops the dispatcher edited, with
        // null clearing the override. The server applies it after stopSequence (so
        // same-submit refs resolve) and does not clear ETAs / is_optimized.
        const serviceTimeEntries: IStopServiceTimeEntry[] = stopStates
            .filter((stop) => stop.status !== "removed" && stop.key in serviceTimeOverrides)
            .map((stop) => ({ ...toStopRef(stop), service_minutes: serviceTimeOverrides[stop.key] }));
        const stopServiceTimes = serviceTimeEntries.length > 0 ? serviceTimeEntries : undefined;

        // Build the daily route object - either from existing or create new
        const dailyRouteData = dailyRoute
            ? {
                  ...dailyRoute,
                  route_id: selectedRouteId!,
                  route_date: values.route_date,
                  vehicle_id: values.vehicle_id ?? null,
                  driver_user_id: selectedDriver?.id ?? null,
              }
            : {
                  route_id: selectedRouteId!,
                  route_date: values.route_date,
                  vehicle_id: values.vehicle_id ?? null,
                  driver_user_id: selectedDriver?.id ?? null,
              };

        const payload: IProcessDailyRouteDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            dailyRoutes: [
                {
                    dailyRoute: dailyRouteData,
                    orders: addedOrderIds.length > 0 ? addedOrderIds : undefined,
                    removeOrders: removedOrderIds.length > 0 ? removedOrderIds : undefined,
                    customers: addedCustomerIds.length > 0 ? addedCustomerIds : undefined,
                    removeStops: removedStopIds.length > 0 ? removedStopIds : undefined,
                    seedRouteCustomers: seedRouteCustomers ? true : undefined,
                    stopSequence,
                    stopServiceTimes,
                },
            ],
        };

        socket.emit(EndpointsEnum.RoutesProcessDailyRoute, payload);
    };

    const onProcessDailyRoute = (_data: IOnProcessDailyRouteDTO) => {
        const notification: INotification = {
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
            message: "successfully_processed_daily_route",
        };

        uiContext.notifyFn(null, notification);

        props.cancelFn(true);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.RoutesOnProcessDailyRoute, fn: onProcessDailyRoute },
        { endpoint: EndpointsEnum.VendorsOnGetVendors, fn: onGetVendors },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getVehicles();
        getRoutes();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
            // Clear search timeout on unmount
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const atLeastOneStopMessageKey = supplierModel.has_van_sell ? "at_least_one_order_or_customer_required" : "at_least_one_order_required";

    const continueSubmit = (values: { route_date: string; route_id: number | undefined; vehicle_id: number | undefined }) => {
        const activeStops = stopStates.filter((stop) => stop.status !== "removed");

        // Validate at least one stop (order or customer) is assigned; the server
        // also accepts seedRouteCustomers alone for a new route.
        if (activeStops.length === 0 && !seedRouteCustomers) {
            const notification: INotification = {
                message: atLeastOneStopMessageKey,
                position: NotificationPositionEnum.bc,
                level: NotificationLevelEnum.warning,
            };
            uiContext.notifyFn(null, notification);
            return;
        }

        // Check for orders needing status change (not in READY_FOR_DELIVERY or
        // READY_FOR_PICKUP). Only ORDER rows apply — customer stops have no order yet.
        const activeOrdersList = activeStops.filter((stop) => stop.order).map((stop) => stop.order!);
        const needsStatusChange = DailyRouteService.getOrdersNeedingStatusChange(activeOrdersList);

        if (needsStatusChange.length > 0) {
            setOrdersNeedingStatusChange(needsStatusChange);
            setPendingFormValues(values);
            setShowStatusWarningModal(true);
        } else {
            processDailyRoute(values);
        }
    };

    const handleSubmit = (values: { route_date: string; route_id: number | undefined; vehicle_id: number | undefined }) => {
        // Reorder-only submit (route already in progress): the order checks below
        // concern adding orders to a route, so skip straight to processing.
        if (!canChangeRoute()) {
            processDailyRoute(values);
            return;
        }

        // Check for orders whose "Do Not Ship Before" date is after the route date
        const activeOrders = stopStates.filter((stop) => stop.status !== "removed" && stop.order).map((stop) => stop.order!);
        const { excludedOrders } = DailyRouteService.filterDoNotShipBeforeOrders(activeOrders, values.route_date);

        if (excludedOrders.length > 0) {
            // Show confirmation modal instead of hard-removing
            setDoNotShipExcludedOrders(excludedOrders);
            setPendingExcludedIds(new Set(excludedOrders.map((o) => o.id)));
            setPendingFormValues(values);
            setShowDoNotShipWarningModal(true);
            return;
        }

        continueSubmit(values);
    };

    const confirmProceed = () => {
        setShowStatusWarningModal(false);
        if (pendingFormValues) {
            processDailyRoute(pendingFormValues);
        }
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const cancelStatusWarning = () => {
        setShowStatusWarningModal(false);
        setPendingFormValues(null);
        setOrdersNeedingStatusChange([]);
    };

    const confirmDoNotShipRemoval = () => {
        setShowDoNotShipWarningModal(false);
        dropServiceOverrides(stopStates.filter((stop) => stop.order && pendingExcludedIds.has(stop.order.id)).map((stop) => stop.key));
        // Remove excluded orders from state
        setStopStates(
            (prev) =>
                prev
                    .map((stop) => {
                        if (!stop.order || !pendingExcludedIds.has(stop.order.id)) return stop;
                        if (stop.status === "added") return null;
                        return { ...stop, status: "removed" as const };
                    })
                    .filter(Boolean) as IStopRowState[]
        );
        if (pendingFormValues) {
            continueSubmit(pendingFormValues);
        }
        setPendingExcludedIds(new Set());
        setDoNotShipExcludedOrders([]);
    };

    const cancelDoNotShipWarning = () => {
        setShowDoNotShipWarningModal(false);
        setPendingFormValues(null);
        setPendingExcludedIds(new Set());
        setDoNotShipExcludedOrders([]);
    };

    const formik = useFormik({
        initialValues: {
            route_id: dailyRoute?.route_id || undefined,
            route_date: dailyRoute?.route_date || "",
            vehicle_id: dailyRoute?.vehicle_id || undefined,
        },
        onSubmit: (values) => {
            handleSubmit(values);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.route_date) {
                errors.route_date = "Required";
            }

            if (!selectedRouteId) {
                errors.route_id = "Required";
            }

            // Require at least one active stop (order or customer); seeding route
            // customers also satisfies this (the server adds the stops).
            const activeStopCount = stopStates.filter((stop) => stop.status !== "removed").length;
            if (activeStopCount === 0 && !seedRouteCustomers) {
                errors.stops = atLeastOneStopMessageKey;
            }

            setIsDisabled(Object.keys(errors).length > 0);

            return errors;
        },
    });

    // Run validation when selectedRouteId changes to update save button state
    useEffect(() => {
        formik.validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRouteId]);

    // Run validation when the stop list changes to update save button state
    useEffect(() => {
        formik.validateForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopStates, seedRouteCustomers]);

    // Split stops by status for display
    const activeStops = stopStates.filter((stop) => stop.status !== "removed");
    const removedStops = stopStates.filter((stop) => stop.status === "removed");

    // Mid-route (header fields read-only) Save appears only for a reorder or a
    // service-time edit, both of which the server accepts while in_progress.
    const hasPendingStopEdits = canReorder() && (hasReordered || hasServiceTimeChanges);

    // Service (dwell) minutes for a stop. Editable under the same gate as drag
    // reorder (not_started / in_progress) because mid-route reopt re-reads the
    // value; static text once the route is done. Placeholder = inherited value
    // (customer default, else supplier default).
    const renderServiceTimeControl = (stop: IStopRowState) => {
        const vendorDefault = stop.order?.Vendor?.default_service_minutes ?? stop.vendor?.default_service_minutes;
        const inherited = DailyRouteService.getEffectiveServiceMinutes(null, supplierModel.default_stop_service_minutes, vendorDefault);
        const currentOverride = stop.key in serviceTimeOverrides ? serviceTimeOverrides[stop.key] : (stop.serviceMinutes ?? null);

        if (!canReorder()) {
            return <span className="stop-service text-muted small text-nowrap ms-2">{currentOverride ?? inherited} min</span>;
        }

        return (
            <span className="stop-service d-flex align-items-center text-nowrap ms-2">
                <OwFnInput
                    fn={(e: React.ChangeEvent<HTMLInputElement>) => setServiceOverride(stop, e.target.value)}
                    name={`service_minutes_${stop.key}`}
                    type="number"
                    min={0}
                    step={1}
                    noOuterDiv={true}
                    skipTranslate={true}
                    className="stop-service-input"
                    value={currentOverride ?? ""}
                    placeholder={String(inherited)}
                />
                <span className="text-muted small ms-1">min</span>
                {currentOverride !== null && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-muted p-0 ms-1 pointer"
                        title={intl.formatMessage({ id: "clear_service_time_override" })}
                        onClick={() => setServiceOverride(stop, "")}>
                        <i className="fas fa-undo" />
                    </button>
                )}
            </span>
        );
    };

    const renderStopContent = (stop: IStopRowState) => {
        if (stop.order) {
            return (
                <>
                    <span className="flex-grow-1">
                        {stop.status === "added" && <i className="fas fa-plus-circle text-success me-2" />}
                        {stop.order.name}
                    </span>
                    {renderServiceTimeControl(stop)}
                    {canChangeRoute() && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 ms-2 pointer"
                            onClick={() => removeStop(stop.key)}>
                            <i className="fas fa-times" />
                        </button>
                    )}
                </>
            );
        }

        return (
            <>
                <span className="flex-grow-1">
                    {stop.status === "added" && <i className="fas fa-plus-circle text-success me-2" />}
                    <i className="fas fa-store me-2 text-muted" />
                    {stop.vendor?.name}
                    {!stop.vendor?.ship_to_id && (
                        <span className="text-warning small ms-2">
                            <i className="fas fa-exclamation-triangle me-1" />
                            <OwLabel title="customer_stop_no_address_warning" />
                        </span>
                    )}
                </span>
                {renderServiceTimeControl(stop)}
                {canChangeRoute() && (
                    <button type="button" className="btn btn-sm btn-link text-danger p-0 ms-2 pointer" onClick={() => removeStop(stop.key)}>
                        <i className="fas fa-times" />
                    </button>
                )}
            </>
        );
    };

    return (
        <div className={"DailyRouteSidebar p-2"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-map-marked-alt"} title={"daily_route"} />
                    {dailyRoute?.name && <OwTitle title={dailyRoute.name} tag="h4" disableTranslation={true} defaultMargins={false} />}
                    <hr />
                </div>
            </div>

            <div>{!canChangeRoute() && <OwPill title="read_only_daily_route" className="bg-danger text-white mb-2" />}</div>

            <div className="row">
                <form onSubmit={formik.handleSubmit}>
                    {/* Route Selection */}
                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"route"} isBold={true} />
                        </div>
                        <div>
                            {isLoadingRoutes ? (
                                <div className="text-center p-2">
                                    <i className="fas fa-spinner fa-spin text-muted" />
                                </div>
                            ) : canChangeRoute() ? (
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedRouteId || ""}
                                    onChange={(e) => handleRouteChange(Number(e.target.value))}>
                                    <option value="" disabled>
                                        Select route...
                                    </option>
                                    {availableRoutes.map((route) => (
                                        <option key={route.id} value={route.id}>
                                            {route.name}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="">{getCurrentRouteName()}</div>
                            )}
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"route_date"} isBold={true} />
                        </div>
                        <div className={""}>
                            <OwDateInput
                                fn={(date: Moment) => {
                                    const e = {
                                        target: {
                                            value: date.format("YYYY-MM-DD"),
                                            name: "route_date",
                                        },
                                    };

                                    formik.handleChange(e);
                                }}
                                selectedDate={formik.values.route_date}
                                disabled={!canChangeRoute()}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"vehicle"} isBold={true} />
                        </div>
                        <div>
                            <OwSelectOption
                                name="vehicle_id"
                                options={vehicles}
                                selectedOption={{ vehicle_id: formik.values.vehicle_id }}
                                selectedOptionProp="vehicle_id"
                                valueProp="id"
                                titleProp="name"
                                fn={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                    const value = e.target.value;
                                    formik.setFieldValue("vehicle_id", value ? Number(value) : undefined);
                                }}
                                showBlankFirst={true}
                                isDisabled={!canChangeRoute()}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"driver"} isBold={true} />
                        </div>
                        <div>
                            <TypeaheadFn
                                lookupFn={searchUsers}
                                availableOptions={availableUsers.map((token) => token.User).filter(Boolean) as IUser[]}
                                selectedOption={selectedDriver}
                                valueProp="id"
                                selectedOptionFn={handleDriverSelected}
                                emitSelectionSetup={{ emitObject: true }}
                                titleProp="name"
                                subTitleProp="email"
                                renderFn={renderAvailableUser}
                                placeholder="search_for_driver"
                                clearSelectionFn={clearDriver}
                                disabled={!canChangeRoute()}
                            />
                        </div>
                    </div>

                    {/* Order Search */}
                    <div className={"d-flex flex-column mb-3"}>
                        <div>
                            <OwLabel title={"add_order"} isBold={true} />
                        </div>
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search by order number..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                disabled={!canChangeRoute()}
                            />
                            {isSearching && (
                                <div className="position-absolute end-0 top-50 translate-middle-y me-2">
                                    <i className="fas fa-spinner fa-spin text-muted" />
                                </div>
                            )}
                            {/* Search Results Dropdown */}
                            {searchResults.length > 0 && (
                                <div className="search-results-dropdown">
                                    {searchResults.map((order) => {
                                        const validation = DailyRouteService.validateOrderForDailyRoute(order);
                                        const dnsBlocked =
                                            formik.values.route_date &&
                                            DailyRouteService.isBeforeDoNotShipDate(order, formik.values.route_date);
                                        const isDisabledOrder = validation.isBlocked || dnsBlocked;
                                        return (
                                            <div
                                                key={order.guid}
                                                className={`search-result-item ${isDisabledOrder ? "disabled" : ""}`}
                                                onClick={() => !isDisabledOrder && addOrder(order)}
                                                title={
                                                    validation.isBlocked
                                                        ? validation.blockReason
                                                        : dnsBlocked
                                                          ? "Do Not Ship Before date is after route date"
                                                          : undefined
                                                }>
                                                <span>{order.name}</span>
                                                {order.Vendor && <small className="text-muted ms-2">({order.Vendor.name})</small>}
                                                {validation.isBlocked && (
                                                    <span className="ms-2 text-danger">
                                                        <i className="fas fa-ban me-1" />
                                                        <OwLabel title={validation.blockReason!} />
                                                    </span>
                                                )}
                                                {!validation.isBlocked && dnsBlocked && (
                                                    <span className="ms-2 text-warning">
                                                        <i className="fas fa-calendar-exclamation me-1" />
                                                        <OwLabel title="do_not_ship_before_route_date" />
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Customers (order-less stops; van-sell suppliers only) */}
                    {supplierModel.has_van_sell && (
                        <div className={"d-flex flex-column mb-3"}>
                            <div>
                                <OwLabel title={"customers"} isBold={true} />
                            </div>
                            <div>
                                <TypeaheadFn
                                    lookupFn={searchCustomers}
                                    availableOptions={availableCustomers}
                                    selectedOptionFn={handleCustomerSelected}
                                    emitSelectionSetup={{ emitObject: true }}
                                    titleProp="name"
                                    renderFn={renderAvailableCustomer}
                                    placeholder="search_for_customer"
                                    resetTypeahead={resetCustomerTypeahead}
                                    resetTypeaheadCallbackFn={() => setResetCustomerTypeahead(false)}
                                    disabled={!canChangeRoute()}
                                />
                            </div>
                            <div className="mt-2">
                                <OwButton
                                    type={"button"}
                                    className={seedRouteCustomers ? "btn-success" : ""}
                                    leftIconClass={seedRouteCustomers ? "fas fa-check" : "fas fa-users"}
                                    title={seedRouteCustomers ? "route_customers_will_be_added" : "add_route_customers"}
                                    fn={() => setSeedRouteCustomers(!seedRouteCustomers)}
                                    disabled={!canChangeRoute() || !selectedRouteId}
                                />
                            </div>
                        </div>
                    )}

                    {/* Stops on Route */}
                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={supplierModel.has_van_sell ? "stops_on_route" : "orders_on_route"} isBold={true} />
                        </div>
                        <div className="order-list">
                            {stopStates.length === 0 ? (
                                <div className="text-muted small p-2 text-center">
                                    <OwLabel title={"no_orders_on_route"} />
                                </div>
                            ) : (
                                <>
                                    {/* Active stops (existing + added) in sequence order, drag-to-reorder */}
                                    {activeStops.map((stop, index) => (
                                        <DraggableStopRow
                                            key={stop.key}
                                            stop={stop}
                                            index={index}
                                            canDrag={canReorder()}
                                            onDropOn={moveStop}>
                                            {renderStopContent(stop)}
                                        </DraggableStopRow>
                                    ))}
                                    {/* Removed stops pinned at the bottom, not draggable */}
                                    {removedStops.map((stop) => (
                                        <div
                                            key={stop.key}
                                            className="order-item d-flex align-items-center justify-content-between removed">
                                            <span className="text-decoration-line-through text-muted">
                                                {stop.order?.name || stop.vendor?.name}
                                            </span>
                                            <button type="button" className="btn btn-sm btn-link p-0" onClick={() => restoreStop(stop.key)}>
                                                <i className="fas fa-undo" />
                                            </button>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                        {/* Warning when no active stops */}
                        {activeStops.length === 0 && !seedRouteCustomers && canChangeRoute() && (
                            <div className="text-danger small mt-1">
                                <i className="fas fa-exclamation-circle me-1" />
                                <OwLabel title={atLeastOneStopMessageKey} />
                            </div>
                        )}
                    </div>

                    <div>
                        {(canChangeRoute() || hasPendingStopEdits) && (
                            <OwButton type={"submit"} title={"save"} disableOnActive={true} disabled={isDisabled} />
                        )}
                        {!canChangeRoute() && (
                            <OwButton
                                type={"button"}
                                className={"btn-danger" + (hasPendingStopEdits ? " ms-2" : "")}
                                title={"close"}
                                disableOnActive={true}
                                fn={cancel}
                            />
                        )}
                    </div>
                </form>
            </div>

            {/* Do Not Ship Before Warning Modal */}
            <Modal
                isOpen={showDoNotShipWarningModal}
                contentLabel="Do Not Ship Before Warning"
                shouldCloseOnOverlayClick={false}
                className="modalContentClass d-flex justify-content-center"
                overlayClassName="modalOverlayClass">
                <DailyRouteDoNotShipWarningModal
                    excludedOrders={doNotShipExcludedOrders}
                    routeDate={pendingFormValues?.route_date || ""}
                    proceedFn={confirmDoNotShipRemoval}
                    cancelFn={cancelDoNotShipWarning}
                />
            </Modal>

            {/* Status Warning Modal */}
            <Modal
                isOpen={showStatusWarningModal}
                contentLabel="Order Status Warning"
                shouldCloseOnOverlayClick={false}
                className="modalContentClass d-flex justify-content-center"
                overlayClassName="modalOverlayClass">
                <DailyRouteStatusWarningModal
                    ordersNeedingStatusChange={ordersNeedingStatusChange}
                    proceedFn={confirmProceed}
                    cancelFn={cancelStatusWarning}
                />
            </Modal>
        </div>
    );
};
